"use client";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import { trackEvent } from "~/connectors/analytics.connector";
import { AnalyticsEvents } from "~/connectors/analyticsEvents";
import { customerNotificationsService } from "~/connectors/notifications.connector";


interface NotifyParamHandlerProps {
  refresh: () => Promise<void>;
}

/**
 * Handles the `?notify=<notificationId>` URL parameter.
 * When present, marks the notification as read, refreshes the count,
 * removes the param from the URL, and fires an analytics event.
 */
export default function NotifyParamHandler({
  refresh,
}: NotifyParamHandlerProps) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    const notificationId = searchParams.get("notify");
    if (!notificationId) return;

    const handle = async () => {
      try {
        await customerNotificationsService.markAsRead(notificationId);
        await refresh();
        trackEvent(AnalyticsEvents.NOTIFICATION_PUSH_OPEN, { notificationId });
      } catch {
        // Silently ignore errors
      } finally {
        // Remove the notify param from the URL without reloading
        const params = new URLSearchParams(searchParams.toString());
        params.delete("notify");
        const newUrl = params.toString()
          ? `${pathname}?${params.toString()}`
          : pathname;
        router.replace(newUrl);
      }
    };

    handle();
  }, [searchParams, pathname, router, refresh]);

  return null;
}
